import React, { useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SourceChip from './SourceChip';
import { refItems } from './refKinds';

// A link goes anywhere only on a click, and only to the places listed here (an allowlist, so an
// address written in some odd way is dropped too):
//  - an absolute web address opens in a new tab with no opener and no referrer (this page's own
//    address holds a patient id);
//  - an anchor (a footnote) stays in the page;
//  - an e-mail link works as it is.
// Anything else shows its text and nothing more: the empty href react-markdown leaves for
// javascript:, vbscript: and data: links, and relative paths, which would only take this page
// somewhere it was not built to go.
const WEB = /^https?:\/\//i;
const IN_PAGE_OR_MAIL = /^(#|mailto:)/i;
const LINK = 'text-blue-600 dark:text-blue-400 underline';

// Whether a link has nothing in it to show or click. An image that was left out (see NO_ELEMENTS)
// can be all a link held, and an empty link would still be there to click.
const isBlank = (children) => React.Children.toArray(children)
  .every((child) => typeof child === 'string' && child.trim() === '');

// ---- colored words -------------------------------------------------------------------------------
// An agent colors what it reports (backend prompts.TONES): {red}...{/red} for high priority, {yellow}
// ...{/yellow} for a warning, {green}...{/green} for clear, or the colors the clinician asked for, from
// this palette. Each pair becomes a span of its color, as the agent's Messages posts do (backend
// notify._html): it may wrap bold, a link or a line break, never cross a paragraph. A tag left open, a
// stray one and one inside another pair are dropped, never shown; a color outside the palette and
// anything else in braces is the agent's own text, and code is never touched.
const COLOR_CLASSES = {
  red: 'text-red-600 dark:text-red-400 font-semibold',
  orange: 'text-orange-600 dark:text-orange-400 font-semibold',
  yellow: 'text-amber-600 dark:text-amber-300 font-semibold',
  green: 'text-emerald-700 dark:text-emerald-400 font-semibold',
  blue: 'text-blue-600 dark:text-blue-400 font-semibold',
  purple: 'text-purple-600 dark:text-purple-400 font-semibold',
  gray: 'text-slate-500 dark:text-slate-400 font-semibold',
};
const TONE_TAG = new RegExp(`\\{(\\/?)(${Object.keys(COLOR_CLASSES).join('|')})\\}`);

// A node's children with each text that holds tags split at them: a tag as {tag: 'open' | 'close', color}.
function splitTags(children) {
  const items = [];
  children.forEach((child) => {
    if (child.type !== 'text' || !TONE_TAG.test(child.value)) {
      items.push(child);
      return;
    }
    const pattern = new RegExp(TONE_TAG.source, 'g');
    let last = 0;
    for (let found = pattern.exec(child.value); found; found = pattern.exec(child.value)) {
      if (found.index > last) items.push({ type: 'text', value: child.value.slice(last, found.index) });
      items.push({ tag: found[1] ? 'close' : 'open', color: found[2] });
      last = found.index + found[0].length;
    }
    if (last < child.value.length) items.push({ type: 'text', value: child.value.slice(last) });
  });
  return items;
}

// Each opening tag with the next closing tag of its color around what lies between them (any other tag
// in there dropped), as a span of its color; every other tag dropped.
function pairTags(items) {
  const out = [];
  for (let at = 0; at < items.length; at += 1) {
    const item = items[at];
    if (!item.tag) {
      out.push(item);
    } else if (item.tag === 'open') {
      const end = items.findIndex((other, i) => i > at && other.tag === 'close' && other.color === item.color);
      if (end > at) {
        out.push({ type: 'colored', data: { hName: 'span', hProperties: { dataColor: item.color } },
          children: items.slice(at + 1, end).filter((inner) => !inner.tag) });
        at = end;
      }
    }
  }
  return out;
}

function toneNode(node) {
  if (!Array.isArray(node.children)) return;
  if (node.children.some((child) => child.type === 'text' && TONE_TAG.test(child.value))) {
    node.children = pairTags(splitTags(node.children));
  }
  node.children.forEach(toneNode);
}

// The remark plugin that makes colored words spans of their color (data-color).
export function remarkTones() {
  return (tree) => { toneNode(tree); };
}

// The AI Chat's answer styling (MedStudentAIAssistant), shared by agent results.
const COMPONENTS = {
  p: ({ node, ...props }) => <p className="text-xs md:text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-2" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-slate-900 dark:text-slate-100" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 space-y-1.5 my-2 text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 space-y-1.5 my-2 text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />,
  li: ({ node, ...props }) => <li className="text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />,
  h1: ({ node, children, ...props }) => <h1 className="text-sm md:text-base font-bold text-slate-900 dark:text-slate-100 mt-3 mb-1.5" {...props}>{children}</h1>,
  h2: ({ node, children, ...props }) => <h2 className="text-xs md:text-sm font-bold text-slate-900 dark:text-slate-100 mt-2.5 mb-1" {...props}>{children}</h2>,
  h3: ({ node, children, ...props }) => <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-200 mt-2 mb-1" {...props}>{children}</h3>,
  // Only remarkTones makes spans (no raw HTML is rendered): colored words.
  span: ({ node, ...props }) => <span className={COLOR_CLASSES[props['data-color']]} {...props} />,
  // target and rel come after the spread, so nothing in the text can change them.
  a: ({ node, children, href, ...props }) => {
    if (isBlank(children)) return null;
    if (typeof href === 'string' && WEB.test(href)) {
      return <a {...props} className={LINK} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    }
    if (typeof href === 'string' && IN_PAGE_OR_MAIL.test(href)) {
      return <a {...props} className={LINK} href={href}>{children}</a>;
    }
    return <>{children}</>;
  },
};

const PLUGINS = [remarkGfm, remarkTones];
// Agent text is written by a model that reads faxes and messages anyone can send, and real
// patient details are put back into it before it is shown. A markdown image would make the
// browser fetch an address the sender chose, with those details in it, as soon as it renders. So
// no image is rendered. react-markdown's own URL sanitizer and its refusal of raw HTML stay as
// they are: no urlTransform and no rehype plugin is passed.
const NO_ELEMENTS = ['img'];

// How much of one kind of thing a text may hold and still be parsed as markdown. Past a limit the
// text is shown as it is: every character of it is still there, and the page does not stall. The
// parser's time does not grow with a text's length alone. These climb with the square of their
// count, or fall off a cliff, and a text that reads like a report holds none of them in numbers
// near these. Measured with the real react-markdown and remark-gfm:
//   [                         2,000 nested: 0.15 s     8,000: 1.7 s     25,000: about 30 s
//   lines holding a |           300 rows: 0.2 s        3,000: 1.6 to 6 s    12,000: over 20 s
//   | in all (wide tables)    6,000: 1.1 s             24,000: over 20 s
//   * and _ together          4,000: 0.3 to 0.5 s      16,000: up to 8 s
//   > in a row (nested quotes)  500: 0.05 s            1,000: never ends
// A text just under a limit takes 0.3 s, or 1.2 s for the widest table the pipes limit allows;
// just under every limit at once, about 0.7 s.
const LIMITS = {
  chars: 50000,
  brackets: 2000,
  tableRows: 300,
  pipes: 5000,
  emphasis: 4000,
  quoteDepth: 50,
};

// Whether a text goes past any limit. One pass over it, and it runs only when the text changes.
function tooHeavy(body) {
  if (body.length > LIMITS.chars) return true;
  let brackets = 0;
  let pipes = 0;
  let rows = 0;
  let emphasis = 0;
  let deepest = 0;
  let depth = 0; // > markers so far in this line's start
  let atStart = true; // still among this line's leading spaces and > markers
  let hasPipe = false; // this line holds a |
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i];
    if (ch === '\n') {
      if (hasPipe) rows += 1;
      hasPipe = false;
      atStart = true;
      depth = 0;
    } else {
      if (ch === '[') {
        brackets += 1;
      } else if (ch === '|') {
        pipes += 1;
        hasPipe = true;
      } else if (ch === '*' || ch === '_') {
        emphasis += 1;
      }
      if (atStart) {
        if (ch === '>') {
          depth += 1;
          if (depth > deepest) deepest = depth;
        } else if (ch !== ' ' && ch !== '\t') {
          atStart = false;
        }
      }
    }
  }
  if (hasPipe) rows += 1;
  return brackets > LIMITS.brackets || rows > LIMITS.tableRows || pipes > LIMITS.pipes
    || emphasis > LIMITS.emphasis || deepest > LIMITS.quoteDepth;
}

// ---- sources: the records a reply names -------------------------------------------------------
// A reply's refs (the records it named or used, resolved for the viewer by the agent detail) link the
// handles the text names them by. Each handle in the text that is one of them becomes a SourceChip: a
// chip, or, for a record the viewer can't open, its kind's words ("a fax"), with a capital when they
// start a sentence. A handle that isn't one stays as it is.
// Only the text the elements below hold is touched: react-markdown renders code with components of its
// own, whose text never reaches these, and a link's text stays as it is (a button can't sit in a link).
// Without refs, react-markdown gets exactly what it got before; a text too heavy to parse is shown as it
// is, handles and all (its Sources row still lists them).
// The components for a text with refs are the same for every text, and read its refs from Links: a
// change in the refs (a fresh read, a label that changed) renders the reply's text again rather than
// rebuilding it, so a chip that stays keeps its place, and the focus it may have.

// A handle as the agent writes it (the backend's handles.HANDLE_SHAPE): a kind, an underscore and a
// number. It counts only standing on its own, with no letter, digit or underscore against it.
const HANDLE = /[a-z][a-z_]*_[0-9]+/;
const WORD_CHAR = /[A-Za-z0-9_]/;
const SPACE = /\s/;
const SENTENCE_END = /[.!?]/;
// The elements whose own text is linked, and those among them whose text starts a sentence.
const LINKED = ['p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'del', 'td', 'th'];
const BLOCKS = new Set(['p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'td', 'th']);
// A text's refs, as {table: handle -> ref, onOpen}; null for a text without them.
const Links = React.createContext(null);
// True inside a link, where the elements leave their text as it is.
const InLink = React.createContext(false);

// Whether the words at `start` of `text` start a sentence: nothing but spaces before them in a text
// that starts a block (`first`), or a full stop, a question mark or an exclamation mark.
function startsSentence(text, start, first) {
  let at = start - 1;
  while (at >= 0 && SPACE.test(text.charAt(at))) at -= 1;
  return at < 0 ? first : SENTENCE_END.test(text.charAt(at));
}

// `text` with each handle that is one of the refs made a SourceChip; the text itself when it names
// none. `at` keeps the chips' keys apart from those of the text's neighbours.
function linkText(text, links, first, at) {
  const out = [];
  let last = 0;
  const shape = new RegExp(HANDLE.source, 'g');
  for (let found = shape.exec(text); found; found = shape.exec(text)) {
    const start = found.index;
    const end = start + found[0].length;
    const item = links.table.get(found[0]);
    if (item && !WORD_CHAR.test(text.charAt(start - 1)) && !WORD_CHAR.test(text.charAt(end))) {
      out.push(text.slice(last, start));
      out.push(<SourceChip key={`${at}:${start}`} item={item} onOpen={links.onOpen} inline
                           capital={startsSentence(text, start, first)} />);
      last = end;
    }
  }
  if (!out.length) return text;
  out.push(text.slice(last));
  return out;
}

// An element's children with the handles in their text linked. Elements among them are left alone. A
// block's first text starts a sentence.
function linkChildren(children, links, block) {
  if (typeof children === 'string') return linkText(children, links, block, 0);
  if (!Array.isArray(children)) return children;
  return children.map((child, at) => (typeof child === 'string' ? linkText(child, links, block && at === 0, at) : child));
}

// The component for an element of LINKED: the one above, or the plain element, with its own text linked.
function linked(tag) {
  const Base = COMPONENTS[tag];
  const block = BLOCKS.has(tag);
  return function Linked({ node, children, ...props }) {
    const links = useContext(Links);
    const inLink = useContext(InLink);
    const shown = links && !inLink ? linkChildren(children, links, block) : children;
    return Base ? <Base node={node} {...props}>{shown}</Base> : React.createElement(tag, props, shown);
  };
}

// The link as it is, inside InLink.
function LinkedAnchor(props) {
  const Anchor = COMPONENTS.a;
  return <InLink.Provider value><Anchor {...props} /></InLink.Provider>;
}

const LINKED_COMPONENTS = {
  ...COMPONENTS,
  ...Object.fromEntries(LINKED.map((tag) => [tag, linked(tag)])),
  a: LinkedAnchor,
};

// Props: text; refs (the message's resolved refs, if any); onOpenRef(ref): a chip was pressed.
function AgentMarkdown({ text, refs = null, onOpenRef = null }) {
  const body = typeof text === 'string' ? text : '';
  const links = useMemo(() => {
    const items = refItems(refs);
    return items.length ? { table: new Map(items.map((item) => [item.key, item])), onOpen: onOpenRef } : null;
  }, [refs, onOpenRef]);
  if (tooHeavy(body)) {
    return (
      <div className="ai-markdown overflow-x-auto">
        <div className="text-xs md:text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap break-words">
          {body.replace(new RegExp(TONE_TAG.source, 'g'), '')}
        </div>
      </div>
    );
  }
  const markdown = (
    <ReactMarkdown remarkPlugins={PLUGINS} components={links ? LINKED_COMPONENTS : COMPONENTS} disallowedElements={NO_ELEMENTS}>
      {body}
    </ReactMarkdown>
  );
  return (
    <div className="ai-markdown overflow-x-auto">
      {links ? <Links.Provider value={links}>{markdown}</Links.Provider> : markdown}
    </div>
  );
}

// Memoized: the clinic page re-renders often, and the same text need not be parsed each time. Each read
// of the agent brings its refs as a fresh list, so they are compared by what they hold.
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const REF_FIELDS = ['key', 'kind', 'label', 'available', 'mentioned', 'used'];
const sameRef = (a, b) => a === b || (isObject(a) && isObject(b) && REF_FIELDS.every((field) => a[field] === b[field]));
const sameRefs = (a, b) => a === b
  || (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, at) => sameRef(item, b[at])));

export default React.memo(AgentMarkdown, (before, after) => before.text === after.text
  && before.onOpenRef === after.onOpenRef && sameRefs(before.refs, after.refs));
